import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { resolvePhotoUrl } from '../../utils/photoUrl';
import {
  ARTIFACT_HATCH_CSS, ARTIFACT_LAYER, LAYERS, LAYER_COLOURS, validateEntries,
} from '../../utils/annotationGeometry';
import AnnotationCanvas from '../../components/annotation/AnnotationCanvas';

/**
 * The AI Training Annotation Workbench (/tools/annotation; until 2026-10-13 the
 * "Tagging game" at /tagging-game, which redirects here): outline where the
 * mushroom is, part by part, and where the things that are not a mushroom are
 * (routes/tagging_game.py). The mechanics, API and tables are the game's; only
 * the framing changed, because what it builds is a segmentation training set.
 *
 * Separate from Model verification, which asks what a photograph shows. One
 * photograph at a time, from a queue that wants two annotators per photograph
 * (TARGET_SESSIONS): the pixel-majority of their outlines is the training mask,
 * exported COCO-style through /tagging/export. A photograph is finished when
 * every layer -- cap, stem, gills/pores and the Ignore / Artifact layer -- is
 * either painted or marked "none": "no foreign objects" is then something a
 * person said, and the export can use it as the negative class.
 *
 * Feedback is a quiet confirmation and session statistics: saved this session
 * (reset after ten idle minutes, not by a skip), active days, dataset
 * milestones and the team's coverage. There is no ranking by design --
 * ranking rewards speed, and a fast outline is a poor one. A link from Model
 * verification (?photo=<id>) opens that photograph outside the queue.
 */
export const IDLE_RESET_MS = 10 * 60 * 1000;
const QUEUE_SIZE = 5;
const FX_MS = 1400;
const MAX_DURATION_MS = 86400000;
const SKIP_REASONS = ['no_mushroom', 'too_blurry', 'other'];

export function nextStreak(streak, now) {
  return { count: now - streak.last <= IDLE_RESET_MS ? streak.count + 1 : 1, last: now };
}

/** {layer: 'painted' | 'absent' | 'todo'}: paint wins over a "none" said earlier. */
export function layerStates(entries, absent) {
  const out = {};
  LAYERS.forEach(layer => {
    const painted = (entries || []).some(e => e.layer === layer && (e.geometry?.shapes || []).length > 0);
    out[layer] = painted ? 'painted' : (absent.includes(layer) ? 'absent' : 'todo');
  });
  return out;
}

function stripMeta({ specimen_index: specimenIndex, layer, fertile_type: fertileType, geometry }) {
  return { specimen_index: specimenIndex, layer, fertile_type: fertileType ?? null, geometry };
}

function timeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

export default function AnnotationWorkbench() {
  const { t } = useTranslation('tagging');
  const { t: ta } = useTranslation('annotate');
  const { user } = useAuth();
  const allowed = (user?.roles || []).some(r => !['public', 'read_only'].includes(r));
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedPhoto = searchParams.get('photo');

  const [queue, setQueue] = useState([]);
  const [remaining, setRemaining] = useState(0);
  const [target, setTarget] = useState(2);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(null);
  const [current, setCurrent] = useState(null);
  const [status, setStatus] = useState('idle');
  const [entries, setEntries] = useState([]);
  const [absent, setAbsent] = useState([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const [streak, setStreak] = useState({ count: 0, last: 0 });
  const [fx, setFx] = useState(null);
  const started = useRef(Date.now());
  const imageSize = useRef({});
  const opening = useRef(0);
  const tRef = useRef(t);
  tRef.current = t;

  const loadProgress = useCallback(() => api.get('/tagging/progress', { params: { tz: timeZone() } })
    .then(({ data }) => setProgress(data))
    .catch(() => {}), []);

  const loadQueue = useCallback(() => {
    setLoading(true);
    setError('');
    return api.get('/tagging/queue', { params: { limit: QUEUE_SIZE } })
      .then(({ data }) => {
        setQueue(data.items || []);
        setRemaining(data.remaining || 0);
        if (data.target_sessions) setTarget(data.target_sessions);
      })
      .catch(() => { setQueue([]); setError(tRef.current('loadFailed')); })
      .finally(() => setLoading(false));
  }, []);

  const openPhoto = useCallback((photo, outsideQueue) => {
    const token = ++opening.current;
    setCurrent({ photo, outsideQueue });
    setEntries([]);
    setAbsent([]);
    setMessage('');
    setSkipOpen(false);
    setStatus('loading');
    started.current = Date.now();
    imageSize.current = {};
    api.get(`/photos/${encodeURIComponent(photo.photo_id)}/annotations`)
      .then(({ data }) => {
        if (token !== opening.current) return;
        setEntries((data?.mine || []).map(stripMeta));
        setStatus('ready');
      })
      .catch(() => { if (token === opening.current) setStatus('failed'); });
  }, []);

  useEffect(() => {
    if (!allowed) return;
    loadQueue();
    loadProgress();
  }, [allowed, loadQueue, loadProgress]);

  useEffect(() => {
    if (!allowed || !linkedPhoto) return undefined;
    let live = true;
    api.get(`/photos/${encodeURIComponent(linkedPhoto)}`)
      .then(({ data }) => { if (live) openPhoto({ ...data, photo_id: data?.photo_id || linkedPhoto }, true); })
      .catch(() => {
        if (!live) return;
        setCurrent({ photo: { photo_id: linkedPhoto }, outsideQueue: true });
        setStatus('failed');
      });
    return () => { live = false; };
  }, [allowed, linkedPhoto, openPhoto]);

  useEffect(() => {
    if (allowed && !linkedPhoto && !current && !loading && queue[0]) openPhoto(queue[0], false);
  }, [allowed, linkedPhoto, current, loading, queue, openPhoto]);

  useEffect(() => {
    if (!fx) return undefined;
    const id = window.setTimeout(() => setFx(null), FX_MS);
    return () => window.clearTimeout(id);
  }, [fx]);

  const states = layerStates(entries, absent);
  const unresolved = LAYERS.filter(layer => states[layer] === 'todo');
  const layerNames = layers => layers.map(layer => ta(`layers.${layer}`)).join(', ');

  const next = () => {
    const done = current?.photo.photo_id;
    const wasLinked = current?.outsideQueue;
    setCurrent(null);
    setStatus('idle');
    const rest = queue.filter(p => p.photo_id !== done);
    setQueue(rest);
    if (wasLinked) {
      setSearchParams({}, { replace: true });
      return;
    }
    setRemaining(r => Math.max(0, r - 1));
    if (!rest.length) loadQueue();
  };

  const toggleAbsent = layer => setAbsent(list => (list.includes(layer) ? list.filter(l => l !== layer) : [...list, layer]));

  const complete = async () => {
    if (!current || busy || status !== 'ready') return;
    if (unresolved.length) {
      setMessage(t('incomplete', { layers: layerNames(unresolved) }));
      return;
    }
    const problem = validateEntries(entries);
    if (problem) {
      setMessage(ta(`problems.${problem}`, { defaultValue: problem }));
      return;
    }
    const photoId = current.photo.photo_id;
    setBusy(true);
    setMessage('');
    try {
      await api.put(`/photos/${encodeURIComponent(photoId)}/annotations`, {
        image_width: imageSize.current.width || null,
        image_height: imageSize.current.height || null,
        annotations: entries,
      });
      await api.post('/tagging/sessions', {
        photo_id: photoId,
        status: 'complete',
        absent_layers: LAYERS.filter(layer => states[layer] === 'absent'),
        duration_ms: Math.min(MAX_DURATION_MS, Math.max(0, Math.round(Date.now() - started.current))),
      });
      const now = Date.now();
      const counted = nextStreak(streak, now);
      setStreak(counted);
      setFx({ id: now, count: counted.count });
      loadProgress();
      next();
    } catch (err) {
      const data = err?.response?.data;
      setMessage(data?.layers ? t('incomplete', { layers: layerNames(data.layers) })
        : t('saveFailed', { error: data?.error || '' }));
    } finally {
      setBusy(false);
    }
  };

  const skip = async (reason) => {
    if (!current || busy) return;
    setBusy(true);
    setMessage('');
    try {
      await api.post('/tagging/sessions', { photo_id: current.photo.photo_id, status: 'skipped', skip_reason: reason });
      loadProgress();
      next();
    } catch (err) {
      setMessage(t('saveFailed', { error: err?.response?.data?.error || '' }));
    } finally {
      setBusy(false);
      setSkipOpen(false);
    }
  };

  const onKeyDown = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      complete();
    }
  };

  if (!allowed) {
    return (
      <div style={pageStyle}>
        <h2 style={{ marginTop: 0 }}>{t('title')}</h2>
        <p>{t('notAllowed')}</p>
      </div>
    );
  }

  const me = progress?.me;
  const team = progress?.team;
  const inARow = Date.now() - streak.last <= IDLE_RESET_MS ? streak.count : 0;
  // Annotated by one person but not yet by the second: the consensus mask
  // cannot be built until someone else outlines it.
  const awaiting = team ? Math.max(0, (team.tagged_once || 0) - (team.consensus || 0)) : 0;

  return (
    <div style={pageStyle} onKeyDown={onKeyDown}>
      <style>{FX_CSS}</style>
      <h2 style={{ margin: 0 }}>{t('title')}</h2>
      <p style={mutedStyle}>{t('intro')}</p>
      <p style={mutedStyle}>{t('noScore')}</p>
      {error && <p role="alert" style={errorStyle}>{error}</p>}

      <div className="tagging-layout" style={layoutStyle}>
        <div style={{ minWidth: 0, position: 'relative' }}>
          {fx && (
            <div key={fx.id} data-testid="tagging-fx" className="tagging-fx" role="status" aria-live="polite" style={fxStyle}>
              ✓ {t('done')}{fx.count > 1 && <> · {t('inARow', { count: fx.count })}</>}
            </div>
          )}
          {!current ? (
            loading || queue.length ? <p style={mutedStyle}>{t('loading')}</p> : (
              <div data-testid="tagging-empty" style={cardStyle}>
                <p style={{ marginTop: 0 }}>{t('empty')}</p>
                <button type="button" onClick={loadQueue} title={t('tip.reload')} style={primaryBtn(true)}>{t('reload')}</button>
              </div>
            )
          ) : (
            <div data-testid="tagging-card" style={cardStyle}>
              <div style={metaStyle}>
                {current.photo.record_code && <span dir="ltr">{current.photo.record_code}</span>}
                {current.outsideQueue ? <span>{t('outsideQueue')}</span>
                  : <span>{t('taggedBy', { count: current.photo.completed_sessions || 0, target })}</span>}
                {current.photo.needs_segmentation && <span style={flagStyle}>{t('flagged')}</span>}
                {!loading && <span>{t('remaining', { count: remaining })}</span>}
              </div>

              {status === 'loading' && <p style={mutedStyle}>{t('photoLoading')}</p>}
              {status === 'failed' && <p role="alert" style={errorStyle}>{t('photoLoadFailed')}</p>}
              {status === 'ready' && (
                <AnnotationCanvas key={current.photo.photo_id} src={resolvePhotoUrl(current.photo.s3_url)}
                                  value={entries} onChange={setEntries}
                                  onImageSize={size => { imageSize.current = size; }} />
              )}

              {status === 'ready' && (
                <div data-testid="tagging-checklist" style={checklistStyle}>
                  <strong style={{ fontSize: 13 }}>{t('checklist.title')}</strong>
                  {LAYERS.map(layer => (
                    <div key={layer} data-testid={`tagging-layer-${layer}`} data-state={states[layer]} style={checkRowStyle}>
                      <span aria-hidden="true" style={{ ...swatchStyle, background: layer === ARTIFACT_LAYER ? ARTIFACT_HATCH_CSS : LAYER_COLOURS[layer] }} />
                      <span style={{ flex: 1 }}>{ta(`layers.${layer}`)}</span>
                      <span style={stateStyle(states[layer])}>
                        {states[layer] === 'painted' ? '✓ ' : ''}{t(`checklist.${states[layer]}`)}
                      </span>
                      {states[layer] !== 'painted' && (
                        <button type="button" onClick={() => toggleAbsent(layer)} style={smallBtn}
                                title={states[layer] === 'absent' ? t('tip.undoAbsent') : t('tip.markAbsent')}>
                          {states[layer] === 'absent' ? t('checklist.undoAbsent') : t('checklist.markAbsent')}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {message && <p role="alert" style={errorStyle}>{message}</p>}

              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" data-testid="tagging-complete" onClick={complete}
                        disabled={busy || status !== 'ready'} title={t('tip.complete')} style={primaryBtn(!busy && status === 'ready')}>
                  {busy ? t('saving') : t('complete')}
                </button>
                <kbd style={kbdStyle}>{t('completeKey')}</kbd>
                <button type="button" data-testid="tagging-skip" onClick={() => setSkipOpen(o => !o)} disabled={busy} title={t('tip.skip')} style={outlineBtn}>
                  {t('skip')}
                </button>
              </div>
              {skipOpen && (
                <div role="group" aria-label={t('skipTitle')} style={skipStyle}>
                  <span style={{ fontSize: 13 }}>{t('skipTitle')}</span>
                  {SKIP_REASONS.map(reason => (
                    <button key={reason} type="button" onClick={() => skip(reason)} disabled={busy} style={outlineBtn}
                            title={{
                              no_mushroom: t('tip.skipReasons.no_mushroom'),
                              too_blurry: t('tip.skipReasons.too_blurry'),
                              other: t('tip.skipReasons.other'),
                            }[reason]}>
                      {t(`skipReasons.${reason}`)}
                    </button>
                  ))}
                  <button type="button" onClick={() => setSkipOpen(false)} title={t('tip.cancel')} style={smallBtn}>{t('cancel')}</button>
                </div>
              )}
            </div>
          )}
        </div>

        <aside data-testid="tagging-progress" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <section style={panelStyle}>
            <h3 style={panelTitle}>{t('progress.title')}</h3>
            <div style={statGrid}>
              <Stat label={t('progress.today')} value={me?.today} />
              <Stat label={t('progress.completed')} value={me?.completed} />
              <Stat label={t('progress.inARow')} value={inARow} testid="tagging-in-a-row" />
              <Stat label={t('progress.skipped')} value={me?.skipped} />
              <Stat label={t('progress.dayStreak')} value={me?.day_streak} testid="tagging-day-streak" />
              <Stat label={t('progress.bestDayStreak')} value={me?.best_day_streak} />
            </div>
          </section>

          {team && (
            <section style={panelStyle}>
              <h3 style={panelTitle}>{t('team.title')}</h3>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <Ring fraction={team.photos ? team.consensus / team.photos : 0} />
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1 }}>
                  <Coverage label={t('team.consensus', { target: team.target_sessions })} done={team.consensus} total={team.photos}
                            summary={t('team.of', { done: team.consensus, total: team.photos })} testid="tagging-team-consensus" />
                  <Coverage label={t('team.once')} done={team.tagged_once} total={team.photos}
                            summary={t('team.of', { done: team.tagged_once, total: team.photos })} testid="tagging-team-once" />
                  <div style={{ fontSize: 12, display: 'flex', justifyContent: 'space-between', gap: 6 }}>
                    <span>{t('team.awaiting')}</span>
                    <strong data-testid="tagging-team-awaiting" dir="ltr">{awaiting}</strong>
                  </div>
                </div>
              </div>
            </section>
          )}

          {me && (
            <section style={panelStyle}>
              <h3 style={panelTitle}>{t('badges.title')}</h3>
              {me.badges.map(badge => (
                <div key={badge.key} data-testid={`tagging-badge-${badge.key}`} data-earned={String(badge.earned)}
                     style={{ ...badgeRow, opacity: badge.earned ? 1 : 0.6 }}>
                  <span aria-hidden="true">{badge.earned ? '🏅' : '○'}</span>
                  <span style={{ flex: 1 }}>{t(`badges.${badge.key}`)}</span>
                  <small style={mutedStyle}>{t('badges.progress', { progress: badge.progress, goal: badge.goal })}</small>
                </div>
              ))}
            </section>
          )}

          {me && (
            <section style={panelStyle}>
              <h3 style={panelTitle}>{t('layersTitle')}</h3>
              {LAYERS.map(layer => (
                <div key={layer} style={badgeRow}>
                  <span aria-hidden="true" style={{ ...swatchStyle, background: layer === ARTIFACT_LAYER ? ARTIFACT_HATCH_CSS : LAYER_COLOURS[layer] }} />
                  <span style={{ flex: 1 }}>{ta(`layers.${layer}`)}</span>
                  <strong>{me.layers?.[layer] ?? 0}</strong>
                </div>
              ))}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div style={statStyle}>
      <strong data-testid={testid} style={{ fontSize: 20 }}>{value ?? '–'}</strong>
      <span style={{ fontSize: 12, color: 'var(--c-text-muted-fg)' }}>{label}</span>
    </div>
  );
}

function Coverage({ label, done, total, summary, testid }) {
  const fraction = total ? Math.min(1, done / total) : 0;
  return (
    <div style={{ fontSize: 12 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 6 }}>
        <span>{label}</span>
        <span data-testid={testid} aria-label={summary} dir="ltr">{done} / {total}</span>
      </div>
      <div style={barTrack}>
        <div className="tagging-bar" style={{ ...barFill, width: `${Math.round(fraction * 100)}%` }} />
      </div>
    </div>
  );
}

function Ring({ fraction }) {
  const r = 26;
  const length = 2 * Math.PI * r;
  const shown = Math.max(0, Math.min(1, fraction));
  return (
    <svg width="64" height="64" viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" style={{ stroke: 'var(--c-border)' }} />
      <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" strokeLinecap="round" className="tagging-ring"
              strokeDasharray={`${length * shown} ${length}`} transform="rotate(-90 32 32)"
              style={{ stroke: 'var(--c-primary)' }} />
      <text x="32" y="36" textAnchor="middle" fontSize="13" style={{ fill: 'var(--c-text-fg)' }}>
        {Math.round(shown * 100)}%
      </text>
    </svg>
  );
}

/* The confirmation fades in and out in place: a quiet "saved", not a reward
 * pop. The coverage bars grow once when the panel first renders. */
const FX_CSS = `
@keyframes tagging-fade {
  0% { opacity: 0; }
  15% { opacity: 1; }
  80% { opacity: 1; }
  100% { opacity: 0; }
}
@keyframes tagging-grow { from { transform: scaleX(0); } to { transform: scaleX(1); } }
.tagging-fx { animation: tagging-fade 1.4s ease-out forwards; }
.tagging-bar { transform-origin: left center; animation: tagging-grow 0.7s ease-out; }
[dir="rtl"] .tagging-bar { transform-origin: right center; }
@media (prefers-reduced-motion: reduce) { .tagging-fx, .tagging-bar { animation: none; } }
@media (max-width: 900px) { .tagging-layout { grid-template-columns: minmax(0, 1fr) !important; } }
`;

const pageStyle = { padding: 16, display: 'flex', flexDirection: 'column', gap: 8, color: 'var(--c-text-fg)' };
const layoutStyle = { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 290px', gap: 16, alignItems: 'start' };
const mutedStyle = { margin: 0, fontSize: 13, color: 'var(--c-text-muted-fg)' };
const errorStyle = { margin: 0, fontSize: 13, color: 'var(--c-danger-fg)', background: 'var(--c-danger-soft)', borderRadius: 6, padding: '6px 10px' };
const cardStyle = { background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, padding: 12, display: 'flex', flexDirection: 'column', gap: 10 };
const metaStyle = { display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 13, color: 'var(--c-text-body-fg)' };
const flagStyle = { color: 'var(--c-warning-text-fg)', background: 'var(--c-warning-soft)', borderRadius: 6, padding: '1px 8px' };
const checklistStyle = { display: 'flex', flexDirection: 'column', gap: 4, border: '1px solid var(--c-border)', borderRadius: 8, padding: 10 };
const checkRowStyle = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 };
const swatchStyle = { display: 'inline-block', width: 14, height: 14, borderRadius: 3, border: '1px solid rgba(0,0,0,0.3)' };
const stateStyle = state => ({
  fontSize: 12, fontWeight: 600,
  color: state === 'painted' ? 'var(--c-success-fg)' : state === 'absent' ? 'var(--c-text-muted-fg)' : 'var(--c-warning-text-fg)',
});
const smallBtn = { background: 'none', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '2px 8px', cursor: 'pointer', fontSize: 12, color: 'var(--c-text-fg)' };
const outlineBtn = { background: 'var(--c-surface)', color: 'var(--c-text-fg)', border: '1px solid var(--c-border-strong)', borderRadius: 6, padding: '6px 12px', cursor: 'pointer', fontSize: 13 };
const primaryBtn = enabled => ({ background: enabled ? 'var(--c-primary)' : 'var(--c-text-faint)', color: 'var(--c-on-accent)',
                                 border: 'none', borderRadius: 6, padding: '8px 18px', cursor: enabled ? 'pointer' : 'default', fontSize: 14, fontWeight: 600 });
const kbdStyle = { fontSize: 11, color: 'var(--c-text-muted-fg)', border: '1px solid var(--c-border)', borderRadius: 4, padding: '1px 5px' };
const skipStyle = { display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', background: 'var(--c-primary-soft)', borderRadius: 8, padding: 8 };
const panelStyle = { background: 'var(--c-surface)', border: '1px solid var(--c-border)', borderRadius: 10, padding: 12 };
const panelTitle = { margin: '0 0 8px', fontSize: 14 };
const statGrid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 8 };
const statStyle = { display: 'flex', flexDirection: 'column', gap: 2 };
const badgeRow = { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, padding: '3px 0' };
const barTrack = { height: 6, borderRadius: 3, background: 'var(--c-border)', overflow: 'hidden', marginTop: 3 };
const barFill = { height: '100%', background: 'var(--c-primary)', borderRadius: 3 };
const fxStyle = { position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none',
                  whiteSpace: 'nowrap', background: 'var(--c-surface)', color: 'var(--c-success-fg)',
                  border: '1px solid var(--c-success-fg)', boxShadow: '0 2px 8px rgba(0,0,0,0.12)', borderRadius: 6,
                  padding: '4px 12px', fontSize: 13, fontWeight: 600 };
